import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { AgEditorComponent } from '../../../../global/gx-ag-grid/ag-editor.component';
import { Location } from '../../../../../models/locations-models/location';
import { FeedbackService } from '../../../../../core/feedback.service';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ICellEditorParams } from 'ag-grid-community';
import { LSCField } from '../../../../../models/LSCFields';
import { getFullNameForFieldID } from '../../../location-utils';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { getEnumValues } from '../../../../../core/utilities';
import { copyContentToClipboard } from '../../../../../utils/miscellaneous';
import { MatIcon } from '@angular/material/icon';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatFormField } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { LetDirective } from '@ngrx/component';

export interface BusinessHoursTControl {
  [LSCField.mondayHours]: FormControl<string|null>,
  [LSCField.tuesdayHours]: FormControl<string|null>,
  [LSCField.wednesdayHours]: FormControl<string|null>,
  [LSCField.thursdayHours]: FormControl<string|null>,
  [LSCField.fridayHours]: FormControl<string|null>,
  [LSCField.saturdayHours]: FormControl<string|null>,
  [LSCField.sundayHours]: FormControl<string|null>
}

@Component({
    selector: 'app-business-hours-editor',
    templateUrl: './business-hours-editor.component.html',
    styleUrls: ['../simple-text-editor/simple-text-editor.component.scss',
                './business-hours-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [LetDirective, CdkTrapFocus, NgClass, MatTabGroup, MatTab, FormsModule, ReactiveFormsModule, MatFormField, MatInput, GxInputDirective, MatIcon]
})
export class BusinessHoursEditorComponent extends AgEditorComponent<Location> {
  @ViewChild('textInput') textInput: ElementRef;
  @ViewChild('tabGroup') tabGroup: MatTabGroup;

  protected readonly getFullNameForFieldID = getFullNameForFieldID;
  protected readonly LSCField = LSCField;
  protected readonly tabNames: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  constructor(protected feedbackService: FeedbackService,
              protected cd: ChangeDetectorRef,
              private fb: FormBuilder) {
    super(feedbackService, cd);
  }

  protected override createEditorFormGroup(params: ICellEditorParams<Location>): FormGroup<BusinessHoursTControl> {
    return this.fb.group(
        {
          [LSCField.mondayHours]: [params?.data?.mondayHours ? params.data.mondayHours : ''],
          [LSCField.tuesdayHours]: [params?.data?.tuesdayHours ? params.data.tuesdayHours : ''],
          [LSCField.wednesdayHours]: [params?.data?.wednesdayHours ? params.data.wednesdayHours : ''],
          [LSCField.thursdayHours]: [params?.data?.thursdayHours ? params.data.thursdayHours : ''],
          [LSCField.fridayHours]: [params?.data?.fridayHours ? params.data.fridayHours : ''],
          [LSCField.saturdayHours]: [params?.data?.saturdayHours ? params.data.saturdayHours : ''],
          [LSCField.sundayHours]: [params?.data?.sundayHours ? params.data.sundayHours : ''],
        },
    );
  }

  /**
   * Return the new location field value
   * Override in child component if needed
   * @protected
   */
  protected override getNewFieldValue() {
    return {
      [LSCField.mondayHours]: this.editorFormGroup.get(LSCField.mondayHours).value ? this.editorFormGroup.get(LSCField.mondayHours).value : null,
      [LSCField.tuesdayHours]:this.editorFormGroup.get(LSCField.tuesdayHours).value ? this.editorFormGroup.get(LSCField.tuesdayHours).value : null,
      [LSCField.wednesdayHours]: this.editorFormGroup.get(LSCField.wednesdayHours).value ? this.editorFormGroup.get(LSCField.wednesdayHours).value : null,
      [LSCField.thursdayHours]: this.editorFormGroup.get(LSCField.thursdayHours).value ? this.editorFormGroup.get(LSCField.thursdayHours).value : null,
      [LSCField.fridayHours]: this.editorFormGroup.get(LSCField.fridayHours).value ? this.editorFormGroup.get(LSCField.fridayHours).value : null,
      [LSCField.saturdayHours]: this.editorFormGroup.get(LSCField.saturdayHours).value ? this.editorFormGroup.get(LSCField.saturdayHours).value : null,
      [LSCField.sundayHours]: this.editorFormGroup.get(LSCField.sundayHours).value ? this.editorFormGroup.get(LSCField.sundayHours).value : null,
    }
  };

  /**
   * HTML helper
   * @param tabName
   * @protected
   */
  protected getLscFieldFromTabName(tabName: string) {
    const lscFields = getEnumValues(LSCField);
    return lscFields.find(lscField => lscField.includes(tabName.toLowerCase()));
  }

  /**
   * A hook to perform any necessary operation just after the GUI for this component has been
   * rendered on the screen.
   * This method is called each time the edit component is activated.
   * This is useful for any logic that requires attachment before executing, such as putting focus
   * on a particular DOM element.
   */
  afterGuiAttached(): void {
    this.textInput?.nativeElement?.focus();
  }

  /**
   * Copy text input to clipboard.
   * @param input Textarea / Input reference
   */
  protected copyTextInput(input: HTMLTextAreaElement | HTMLInputElement) {
    copyContentToClipboard(input, (ok) => {
      ok && this.feedbackService.showSuccessSnackbar({
        data: { userFriendlyMessage: 'Copied' },
        isAddressListSnackbar: true,
      });
    });
  }

}
